import React, { useState, useEffect } from 'react';
import { useSelector } from "react-redux";
import Select from "react-select";
import client from '../../../lib/feathers';

export default function MergeVintageModal({
  onCancelClick,
  onMergeClick,
  libationVintage
}) {
  const user = useSelector((state) => state.auth.user);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [groupedOptions, setGroupedOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load all libations + vintages for this listing, grouped
  useEffect(() => {
    const loadAll = async () => {
      if (!libationVintage?.libation?.listingId) return;
      setIsLoading(true);
      try {
        const service = client.service(`${user.role}/libation`);
        const response = await service.find({
          query: {
            listingId: libationVintage.libation.listingId,
            $limit: 200,
            $sort: { name: 1 },
          },
        });

        const groups = (response.data || response)
          .map((lib) => {
            const vintages = (lib.libationVintages || [])
              .filter((lv) => lv.id !== libationVintage.id)
              .sort((a, b) => {
                const ya = a.vintage?.year || 0;
                const yb = b.vintage?.year || 0;
                if (ya === 9999) return 1;
                if (yb === 9999) return -1;
                return yb - ya;
              })
              .map((lv) => ({
                value: lv.id,
                label: `${lv.vintage?.year === 9999 ? 'NV' : lv.vintage?.year || '?'} — ${lv.counts?.userSips || 0} sips, ${lv.counts?.cellarEntries || 0} in library`,
                searchText: `${lib.name} ${lib.varietal?.name || ''} ${lv.vintage?.year === 9999 ? 'NV' : lv.vintage?.year || ''}`,
              }));

            if (vintages.length === 0) return null;

            const varietal = lib.varietal?.name ? ` (${lib.varietal.name})` : '';
            return {
              label: `${lib.name}${varietal}`,
              options: vintages,
            };
          })
          .filter(Boolean);

        setGroupedOptions(groups);
      } catch (error) {
        console.error("Error loading libations:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadAll();
  }, [libationVintage, user.role]);

  const handleMerge = () => {
    if (selectedTarget && onMergeClick) {
      onMergeClick(selectedTarget.value);
    }
  };

  const sourceYear = libationVintage?.vintage?.year === 9999 ? 'NV' : libationVintage?.vintage?.year;
  const sourceProducer = libationVintage?.libation?.listing?.name || libationVintage?.libation?.listingName || '';

  return (
    <div className="mt-3 sm:mt-5">
      <h3 className="text-lg leading-6 font-medium text-gray-900 text-center" id="modal-headline">
        Merge Vintage into Another
      </h3>
      <div className="mt-4">
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">
                <strong>Source (will be deleted):</strong> {sourceYear} {libationVintage?.libation?.name}{sourceProducer ? ` — ${sourceProducer}` : ''}
              </p>
              <p className="text-sm text-red-700 mt-1">
                All sips, library entries, flight entries, scans, and formats will be moved to the target. The source vintage will then be deleted.
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <label htmlFor="target-select" className="block text-sm font-medium text-gray-700 mb-2">
            Merge into
          </label>
          <Select
            id="target-select"
            options={groupedOptions}
            value={selectedTarget}
            onChange={setSelectedTarget}
            isLoading={isLoading}
            placeholder="Select target wine & vintage..."
            noOptionsMessage={() => "No other vintages found for this listing"}
            filterOption={(option, inputValue) => {
              if (!inputValue) return true;
              const norm = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
              const search = norm(inputValue);
              return norm(option.label).includes(search) || norm(option.data?.searchText).includes(search);
            }}
            className="w-full"
          />
        </div>

        <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
          <button
            type="button"
            onClick={handleMerge}
            disabled={!selectedTarget}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed sm:col-start-2 sm:text-sm"
          >
            Merge &amp; Delete Source
          </button>
          <button
            type="button"
            onClick={onCancelClick}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:mt-0 sm:col-start-1 sm:text-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
